"use client"
import Text from "@/app/(site)/_components/Atoms/Text"
import { Badge } from "@/app/(site)/_components/Molecules/Badge"
import { BulletList } from "@/app/(site)/_components/Molecules/BulletList"
import { useIsMobile } from "@/app/(site)/_utils/_hooks/useIsMobile"
import { getLangCode } from "@/app/(site)/_utils/misc"
import { transformBenefitsList } from "@/app/(site)/_utils/transformData/transformBenfitList"
import { cn } from "@/lib/utils"
import { t } from "@/translations"
import { ImageType } from "@/types"
import {
    CheckoutAPIProduct,
    ImageSize,
    isFullRelatedProduct,
    RelatedProduct
} from "@/types/centra/checkout-api/products/post-products"
import placeholder from "@assets/images/placeholder.png"
import Image from "next/image"
import { usePathname } from "next/navigation"
import React, { useEffect, useState } from "react"
import BaseImage from "../../../Atoms/BaseImage"
import DynamicLink from "../../../Molecules/Link"
import { isProductAvailability } from "../OldProductCards/helper"

const getImageSrc = (
    width: number,
    sources: {
        mini: ImageSize[]
        small: ImageSize[]
        standard: ImageSize[]
        big: ImageSize[]
        full: ImageSize[]
    }
): string => {
    if (width <= 450) {
        return sources?.small?.[0]?.url ?? placeholder
    } else if (width <= 1280) {
        return sources?.standard?.[0]?.url ?? placeholder
    } else {
        return sources?.big?.[0]?.url ?? placeholder
    }
}

type BigProductCardProps = {
    product: CheckoutAPIProduct
    override?: ImageType
}

type CurrentProduct = {
    [key: string]: any
}

/**
 * @todo: this entire product card needs to refactored, we could use one single product card component for the entire site instead of separate components for different components.
 * No separation of concerns, no clear structure, no clear naming conventions, no clear separation of logic and presentation, no regards to different width/height from override images,
 * no alt text fallbacks for images, currentProduct state is not needed.
 *
 */

const BigProductCard: React.FC<BigProductCardProps> = ({ product, override }) => {
    const langCode = getLangCode(usePathname().split("/")[1]) ?? ""
    const isMobile = useIsMobile()
    const [currentProduct, setCurrentProduct] = React.useState<CurrentProduct>({
        image: product?.mediaObjects?.[0]?.sources?.standard?.[0]?.url ?? "",
        name: product?.name ?? "",
        price: product?.price ?? "",
        priceBeforeDiscount: product?.priceBeforeDiscount ?? "",
        available: product?.available ?? "",
        uri: product?.uri ?? "",
        showAsOnSale: product?.showAsOnSale ?? "",
        color_swatch: product?.color_swatch ?? "",
        showAsNew: product?.showAsNew ?? "",
        priceAsNumber: product?.priceAsNumber ?? "",
        imageSources: product?.mediaObjects?.[0]?.sources ?? {},
        product_benefits_list: product?.product_benefits_list ?? []
    })
    const defaultImageSrc =
        override?.asset._ref ||
        product?.mediaObjects?.[0]?.sources?.standard?.[0]?.url ||
        placeholder
    const [imageSrc, setImageSrc] = useState<string>(defaultImageSrc.toString())

    useEffect(() => {
        // Only adjust image source on the client
        const handleResize = () => {
            setImageSrc(
                override?.asset._ref ||
                    getImageSrc(
                        window.innerWidth,
                        product.mediaObjects?.[0]?.sources || {}
                    )
            )
        }

        // Set initial client-side image based on current window size
        handleResize()

        window.addEventListener("resize", handleResize)
        return () => window.removeEventListener("resize", handleResize)
    }, [product.mediaObjects, override, setImageSrc])

    const uri = "/product/" + (product?.uri ?? "")
    const badges = product?.badge
    const colorVariantsAmount =
        (product?.color_swatch?.hex ? 1 : 0) +
        (product?.relatedProducts?.filter(relatedProduct =>
            isFullRelatedProduct(relatedProduct)
                ? relatedProduct?.color_swatch?.hex
                : false
        ).length ?? 0)

    const { name, priceBeforeDiscount, price, showAsOnSale, product_benefits_list } =
        currentProduct

    const benefits = transformBenefitsList(product_benefits_list).splice(0, 3)
    const available = isProductAvailability(product)

    const colorSwatches = [
        ...(product?.relatedProducts?.map(relatedProduct =>
            isFullRelatedProduct(relatedProduct)
                ? {
                      product: relatedProduct,
                      color: relatedProduct?.color_swatch?.hex,
                      sortOrder: Number(relatedProduct?.variant_sort_order_sort_order),
                      uri: relatedProduct?.uri,
                      image: relatedProduct?.lens?.image
                  }
                : {}
        ) || []),
        {
            product,
            color: product?.color_swatch?.hex,
            sortOrder: Number(product?.variant_sort_order_sort_order),
            uri: product?.uri,
            image: product?.lens?.image
        }
    ].filter(swatch => swatch.color)

    const sortedColor = React.useMemo(() => {
        return [...colorSwatches].sort((a, b) => {
            const orderA = a.sortOrder || Infinity
            const orderB = b.sortOrder || Infinity
            return orderA - orderB || (a?.uri || "").localeCompare(b?.uri || "")
        })
    }, [colorSwatches])

    const slicedSortedColors = sortedColor.slice(0, 6)
    const extraColorsCount = sortedColor.length > 6 ? sortedColor.length - 6 : 0

    const hoverImage = product?.mediaObjects[1]?.sources?.standard[0]?.url ?? placeholder
    const productImage = imageSrc || placeholder
    const productImageAlt = override?.alt || name

    function handleChangeVariant(product: RelatedProduct | CheckoutAPIProduct) {
        return () => {
            const newImageSources = product.mediaObjects[0]?.sources ?? []
            setCurrentProduct(prevState => ({
                ...prevState,
                imageSources: newImageSources,
                name: product.name,
                price: product.price,
                priceBeforeDiscount: product.priceBeforeDiscount,
                available: product.available,
                uri: product.uri,
                showAsOnSale: product.showAsOnSale,
                showAsNew: product.showAsNew
            }))
        }
    }

    function handleHover() {
        if (product?.mediaObjects[1]?.sources?.standard[0]?.url && isMobile === false) {
            setCurrentProduct(prevState => ({
                ...prevState,
                imageSources: product?.mediaObjects[1]?.sources
            }))
        }
    }

    function handleMouseLeave() {
        setCurrentProduct(prevState => ({
            ...prevState,
            imageSources: product?.mediaObjects?.[0]?.sources
        }))
    }

    return (
        <div className="group relative">
            <div className={`relative bg-[#f4f5f5]`}>
                {Array.isArray(badges) && badges.length > 0 && (
                    <div className="absolute left-0 right-0 top-0 z-[5] flex flex-wrap gap-2">
                        {badges.map((badge, index) => (
                            <Badge key={index} badge={badge} />
                        ))}
                    </div>
                )}
                <DynamicLink href={uri} prefetch={false}>
                    <div className="mb-16 aspect-square overflow-hidden">
                        <BaseImage
                            src={productImage}
                            alt={productImageAlt || "Product Image"}
                            className={cn(
                                "object-contain",
                                product?.available ? "opacity-100" : "opacity-30"
                            )}
                            onMouseEnter={handleHover}
                            onMouseLeave={handleMouseLeave}
                            fill
                        />
                    </div>
                </DynamicLink>
            </div>
            <div className="grid grid-cols-2">
                <div className="col-span-2 md:col-span-1">
                    <DynamicLink href={uri} prefetch={false}>
                        <Text
                            size="small"
                            weight="regular"
                            className={`md:text-16 ${!available && ""}`}
                        >
                            {name}
                        </Text>
                        <Text
                            size="small"
                            weight="regular"
                            className="mb-8 flex gap-8 text-main-dark-grey md:text-16"
                        >
                            {price}
                            {showAsOnSale ? (
                                <span className="line-through">
                                    {priceBeforeDiscount}
                                </span>
                            ) : (
                                ""
                            )}
                        </Text>
                    </DynamicLink>

                    <div className="h-24 w-full md:h-38">
                        <div
                            className={`hidden ${!isMobile ? "group-hover:block" : " "}`}
                        >
                            <div className="flex w-full flex-wrap items-center">
                                {slicedSortedColors.map((swatch, index) => {
                                    return (
                                        <DynamicLink
                                            href={`/product/${swatch?.product?.uri}`}
                                            className="cursor-pointer"
                                            onMouseEnter={handleChangeVariant(
                                                swatch?.product ?? product
                                            )}
                                            onMouseLeave={handleChangeVariant(product)}
                                            key={index}
                                            prefetch={false}
                                        >
                                            {swatch.color ? (
                                                <div className="m-4 h-24 w-24 rotate-[-50deg] cursor-pointer">
                                                    <div className="relative h-24 w-24">
                                                        {swatch?.image ? (
                                                            <Image
                                                                alt={
                                                                    swatch.product.name ??
                                                                    "Product Image"
                                                                }
                                                                src={swatch.image.url}
                                                                width={100}
                                                                height={100}
                                                                className="absolute inset-0 z-20 rounded-full"
                                                                style={{
                                                                    clipPath:
                                                                        "polygon(0 0, 50% 0, 50% 100%, 0 100%)"
                                                                }}
                                                            />
                                                        ) : (
                                                            <div
                                                                className="absolute inset-0 rounded-full"
                                                                style={{
                                                                    clipPath:
                                                                        "polygon(0 0, 100% 0, 50% 100%, 0 100%)",
                                                                    backgroundColor:
                                                                        swatch?.product
                                                                            ?.lens?.hex ??
                                                                        swatch.color
                                                                }}
                                                            ></div>
                                                        )}
                                                        <div
                                                            className={`absolute inset-0 rounded-full`}
                                                            style={{
                                                                clipPath:
                                                                    "polygon(50% 0, 100% 0, 100% 100%, 50% 100%)",
                                                                backgroundColor:
                                                                    swatch.color
                                                            }}
                                                        ></div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <></>
                                            )}
                                        </DynamicLink>
                                    )
                                })}
                                {extraColorsCount > 0 && (
                                    <Text
                                        size="small"
                                        weight="regular"
                                        className="text-main-dark-grey md:text-16"
                                    >
                                        +{extraColorsCount} {t(langCode, "more")}
                                    </Text>
                                )}
                            </div>
                        </div>
                        {colorVariantsAmount > 0 && (
                            <Text
                                size="small"
                                weight="regular"
                                className={`text-main-dark-grey md:text-16 ${true ? "group-hover:hidden" : ""}`}
                            >
                                {colorVariantsAmount} {t(langCode, "colors")}
                            </Text>
                        )}
                    </div>
                </div>
                <div className="col-span-2 md:col-span-1">
                    {!available ? (
                        <Text
                            size="medium"
                            weight="regular"
                            className="!text-14 text-black md:!text-16"
                        >
                            {t(langCode, "OUT OF STOCK")}
                        </Text>
                    ) : (
                        benefits.length > 0 && <BulletList title={benefits} />
                    )}
                </div>
            </div>
        </div>
    )
}

export default BigProductCard
